import { Async } from '../../components/Async.tsx';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { FreshnessIndicator } from '../../components/FreshnessIndicator.tsx';
import { Panel } from '../../components/States.tsx';
import { formatDateTime, formatNumber } from '../../lib/format.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import { usePageTitle } from '../../lib/page-title.ts';
import { useDataQuality, useSystemStatus, type SystemStatusResponse } from '../../lib/queries.ts';
import { useDashboardParams } from '../../lib/url-state.ts';
import { Controls } from '../controls/Controls.tsx';

type Entity = SystemStatusResponse['data']['freshness']['entities'][number];
type Run = SystemStatusResponse['data']['lastSyncRuns'][number];

export function StatusPage() {
  const t = useT();
  const locale = useLocale();
  usePageTitle(t.status.title);
  const [params] = useDashboardParams();
  const status = useSystemStatus();
  const quality = useDataQuality(params);
  const when = (value: string | null): string => (value ? formatDateTime(value) : t.common.dash);

  const entityColumns: Column<Entity>[] = [
    {
      id: 'entity',
      header: t.status.entity,
      cell: (e) => e.entityType,
      sortValue: (e) => e.entityType,
    },
    {
      id: 'ok',
      header: t.status.lastSuccess,
      cell: (e) => when(e.lastSuccessAt),
      sortValue: (e) => e.lastSuccessAt,
    },
    {
      id: 'fail',
      header: t.status.failures,
      align: 'right',
      cell: (e) => formatNumber(e.consecutiveFailures, locale),
      sortValue: (e) => e.consecutiveFailures,
    },
    { id: 'err', header: t.status.lastError, cell: (e) => e.lastError ?? t.common.dash },
  ];
  const runColumns: Column<Run>[] = [
    { id: 'kind', header: t.status.run.kind, cell: (r) => r.kind },
    { id: 'status', header: t.status.run.status, cell: (r) => t.status.runStatus[r.status] },
    { id: 'start', header: t.status.run.started, cell: (r) => when(r.startedAt) },
    { id: 'end', header: t.status.run.finished, cell: (r) => when(r.finishedAt) },
    { id: 'error', header: t.status.run.error, cell: (r) => r.error ?? t.common.dash },
  ];

  return (
    <>
      <h1 className="text-2xl font-bold tracking-tight">{t.status.title}</h1>
      <Controls meta={quality.data?.meta} compare={false} filters={false} />
      <Panel title={t.status.dataSource}>
        <Async query={status}>
          {({ data, meta }) => (
            <div className="space-y-4">
              <dl className="grid gap-3 sm:grid-cols-3">
                <div>
                  <dt className="text-xs text-muted-foreground">{t.status.dataSource}</dt>
                  <dd className="font-medium">{data.dataSource?.name ?? t.common.dash}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t.status.datasetKind}</dt>
                  <dd className="font-medium">
                    {data.datasetKind ? t.status[data.datasetKind] : t.common.dash}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t.status.freshness}</dt>
                  <dd>
                    <FreshnessIndicator freshness={meta.dataFreshness} />
                  </dd>
                </div>
              </dl>
              <DataTable
                caption={t.status.freshness}
                columns={entityColumns}
                rows={data.freshness.entities}
                rowKey={(e) => e.entityType}
              />
              <p className="text-sm text-muted-foreground">
                {t.status.worker}: {t.status.workerUnknown}
              </p>
            </div>
          )}
        </Async>
      </Panel>
      <Panel title={t.status.syncRuns}>
        <Async query={status}>
          {({ data }) => (
            <DataTable
              caption={t.status.syncRuns}
              columns={runColumns}
              rows={data.lastSyncRuns}
              rowKey={(r) => r.startedAt + r.kind}
            />
          )}
        </Async>
      </Panel>
      <Panel title={t.status.quality}>
        <p className="mb-2 text-sm text-muted-foreground">{t.status.qualityHelp}</p>
        <Async query={quality}>
          {({ data }) => {
            const rows: [string, string][] = [
              [
                t.status.qualityItems.leadsWithoutSource,
                formatNumber(data.leadsWithoutSource, locale),
              ],
              [t.status.qualityItems.wonWithoutItems, formatNumber(data.wonWithoutItems, locale)],
              [
                t.status.qualityItems.wonWithoutCustomer,
                formatNumber(data.wonWithoutCustomer, locale),
              ],
              [
                t.status.qualityItems.lostWithoutReason,
                formatNumber(data.lostWithoutReason, locale),
              ],
              [t.status.qualityItems.unmappedStage, formatNumber(data.unmappedStage, locale)],
              [
                t.status.qualityItems.unknownContainerType,
                t.status.lineItems(
                  data.unknownContainerType.lineItems,
                  data.unknownContainerType.containers,
                ),
              ],
              [
                t.status.qualityItems.autoContainerType,
                t.status.lineItems(
                  data.autoContainerType.lineItems,
                  data.autoContainerType.containers,
                ),
              ],
            ];
            return (
              <dl className="divide-y">
                {rows.map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-2 py-2 text-sm">
                    <dt>{label}</dt>
                    <dd className="font-medium tabular-nums">{value}</dd>
                  </div>
                ))}
              </dl>
            );
          }}
        </Async>
      </Panel>
    </>
  );
}
